import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import * as PostProcessing from '@react-three/postprocessing';
import { forwardRef, useRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface SelectiveBloomEffectProperties extends EffectProperties {
  intensity: number;
  luminanceThreshold: number;
  luminanceSmoothing: number;
  mipmapBlur: boolean;
}

// Create a reusable SelectiveBloom component that can be used in both preview and world
const SelectiveBloomComponent = forwardRef<any, { properties: SelectiveBloomEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  
  // Create a dummy mesh as a placeholder - moved before conditional return
  // This ensures hooks are always called in the same order
  const dummyMesh = useRef(new THREE.Mesh(
    new THREE.SphereGeometry(1, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
  ));
  
  // IMPORTANT: Return null immediately in preview mode to prevent WebGL errors
  if (isPreview) {
    return null;
  }
  
  const { 
    intensity, 
    luminanceThreshold, 
    luminanceSmoothing, 
    mipmapBlur,
    blendFunction 
  } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.SCREEN;

  // NOTE: The SelectiveBloom effect requires a selection of objects to be provided.
  // In a real implementation, this would need to be connected to specific meshes in your scene.
  // This component is a placeholder and will need to be customized for your specific use case.

  return (
    <PostProcessing.SelectiveBloom
      lights={[dummyMesh]}
      intensity={intensity}
      luminanceThreshold={luminanceThreshold}
      luminanceSmoothing={luminanceSmoothing}
      mipmapBlur={mipmapBlur}
      blendFunction={blendFunctionValue}
    />
  );
});

export const SelectiveBloomEffectItem: Effect<SelectiveBloomEffectProperties> = {
  id: '',
  name: 'Selective Bloom',
  type: 'effect',
  subtype: 'SelectiveBloom',
  icon: 'Effect',
  properties: {
    intensity: 1.0,
    luminanceThreshold: 0.9,
    luminanceSmoothing: 0.025,
    mipmapBlur: true,
    blendFunction: 'SCREEN',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.intensity',
      type: 'number',
      label: 'Intensity',
      default: 1.0,
      min: 0,
      max: 3,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.luminanceThreshold',
      type: 'number',
      label: 'Luminance Threshold',
      default: 0.9,
      min: 0,
      max: 1,
      step: 0.01,
      panel: 'effect',
    },
    {
      key: 'properties.luminanceSmoothing',
      type: 'number',
      label: 'Luminance Smoothing',
      default: 0.025,
      min: 0,
      max: 1,
      step: 0.001,
      panel: 'effect',
    },
    {
      key: 'properties.mipmapBlur',
      type: 'boolean',
      label: 'Mipmap Blur',
      default: true,
      panel: 'effect',
    },
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'SCREEN',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    }
  ],
  render: (properties: SelectiveBloomEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <SelectiveBloomComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 